import { useState } from 'react';
import { AgentGlyph } from '../../components/AgentGlyph';
import { CopyButton } from '../../components/CopyButton';
import { Icon } from '../../components/Icon';
import { Link } from '../../components/Link';
import { useCopyState } from '../../hooks/useCopyState';
import { buildAgentDeeplinks } from '../../lib/agentDeeplinks';
import { buildSkuPrompt } from '../../lib/agentPrompt';
import { m } from '../../paraglide/messages';

/** Where a reader with no key in the snippet goes to get one. A public page
 *  sends them to signup; a dashboard surface sends them to their own keys. */
export type SnippetKeySource = 'signup' | 'account';

export interface SnippetActionsProps {
  sku: string;
  /** Human name of what the SKU returns, e.g. "Instagram Profile". */
  title?: string;
  /** The account's key when we hold the plaintext, which turns the key slot
   *  into a real copy action instead of a link. */
  apiKey?: string;
  keySource?: SnippetKeySource;
}

const LINK = 'cursor-pointer text-12 text-text-3 transition-colors hover:text-text';

/** Flattens the copy pill into the row: the actions read as one line of text,
 *  not as a button parked beside two links. */
const FLAT_COPY = 'border-transparent bg-transparent px-0 py-0 text-12 text-text-3 hover:text-text';

const KEY_SOURCES: Record<SnippetKeySource, { to: string; label: () => string }> = {
  signup: { to: '/sign-up', label: m.console_get_free_key },
  account: { to: '/dashboard/keys', label: m.console_get_api_key },
};

/**
 * The handoff row under a snippet: open this exact request in a coding agent,
 * read the prompt that would be sent, and get the key that makes it run.
 *
 * Every deeplink also copies the prompt, for the reason `agentDeeplinks`
 * documents: two of the three targets are custom schemes that fail silently
 * when no handler is registered, so a miss has to degrade to "it is on my
 * clipboard" rather than a dead click. A clipboard that is denied outright
 * opens the prompt row instead, so the text is always reachable by hand.
 */
export const SnippetActions = ({ sku, title, apiKey, keySource = 'signup' }: SnippetActionsProps) => {
  const prompt = buildSkuPrompt({ sku, title });
  const { copied, failed, copy } = useCopyState(prompt);
  const [shown, setShown] = useState(false);
  const open = shown || failed;

  return (
    <div className="border-t border-line-2">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2">
        <div className="flex items-center gap-1.5">
          {/* Both labels occupy the same grid cell so the confirmation cannot
              shove the glyphs sideways under the pointer that just hit one. */}
          <span className="grid text-12 whitespace-nowrap">
            <span className={`col-start-1 row-start-1 text-text-3 ${copied ? 'invisible' : ''}`}>
              {m.console_open_in()}
            </span>
            <span
              className={`col-start-1 row-start-1 flex items-center gap-1 font-medium text-pos ${copied ? '' : 'invisible'}`}
            >
              <Icon name="check" size={12} />
              {m.console_prompt_copied()}
            </span>
          </span>
          {buildAgentDeeplinks(prompt).map(({ id, label, href, newTab }) => (
            <a
              key={id}
              href={href}
              onClick={() => void copy()}
              aria-label={m.console_open_in_agent({ agent: label })}
              title={m.console_open_in_agent({ agent: label })}
              {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="rounded-6 p-1 opacity-75 transition-opacity hover:opacity-100"
            >
              <AgentGlyph id={id} size={15} tone="brand" />
            </a>
          ))}
        </div>
        <div className="flex items-center gap-3.5">
          <button type="button" onClick={() => setShown(!open)} aria-expanded={open} className={LINK}>
            {open ? m.console_hide_prompt() : m.console_show_prompt()}
          </button>
          {apiKey ? (
            <CopyButton value={apiKey} label={m.console_copy_api_key()} className={FLAT_COPY} />
          ) : (
            <Link to={KEY_SOURCES[keySource].to} className={LINK}>
              {KEY_SOURCES[keySource].label()}
            </Link>
          )}
        </div>
      </div>

      {open ? (
        <div className="flex items-start justify-between gap-3 border-t border-line-2 bg-surface-2 px-4 py-2.5">
          <code className="mono min-w-0 flex-1 text-12 leading-relaxed break-words text-text-2">
            {prompt}
          </code>
          <CopyButton value={prompt} label={m.console_copy_prompt()} />
        </div>
      ) : null}

      <span className="sr-only" role="status" aria-live="polite">
        {failed
          ? m.console_copy_failed()
          : copied
            ? m.console_prompt_copied_status()
            : ''}
      </span>
    </div>
  );
};
